import React, { useState, useEffect, useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  CircularProgress,
  Alert,
  Container,
  Chip,
  Tooltip,
  Tabs,
  Tab,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  Stack,
  Card,
  CardContent,
  Skeleton,
  IconButton,
  TextField,
  MenuItem,
  FormControlLabel,
  Autocomplete,
  Divider,
} from '@mui/material';
import {
  PlayArrow as RunIcon,
  Refresh as RefreshIcon,
  CheckCircle as SuccessIcon,
  ErrorOutline as FailedIcon,
  Schedule as ScheduleIcon,
  HourglassBottom as PendingIcon,
  Sync as RunningIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  WarningAmber as StaleIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiService, {
  PipelineSchedule,
  PipelineJobKind,
  CompanyContentPlan,
  CompanyContentPlanInput,
  ContentPlanScope,
} from '../services/api';
import {
  ContentPlanScopesEditor,
  blankContentPlanScope,
} from '../components/ContentPlanScopesEditor';
import { ROUTES_NAMES } from '../components/Layout';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const JOB_LABELS: Record<PipelineJobKind, string> = {
  tokens_all_weekly: 'Tokens — all platforms (weekly safety net)',
  explorer_long_pipeline: 'Explorer long-form pipeline (legacy monolith; not scheduled — run explorer:long:pipeline ad-hoc)',
  explorer_long_discover_classify:
    'Explorer long-form discover + classify only (no sample, transcribe, or analyze)',
  explorer_long_complete_pipeline:
    'Complete long-form pipeline (not scheduled — run explorer:long:complete-pipeline ad-hoc)',
  explorer_long_categories_weekly:
    'Explorer content categories (GPT classify transcripts → aggregate to programs/podcasts; Explorer Programas Categoría)',
  explorer_metrics_youtube: 'YouTube VOD metrics (content + livestreams, unified)',
  company_short_metrics_refresh:
    'Company short metrics — IG, TikTok, YouTube Shorts (OAuth, private content)',
  company_long_metrics_daily:
    'Company long metrics — refreshes views, retention, impressions, CTR and per-video viewer demographics (age/gender) for long-form YouTube private_content; 90-day rolling window, runs per company',
  company_oauth_pipeline_weekly:
    'Company OAuth pipeline — account-level demographics (YT channel + IG follower); weekly Mon 09:00',
  company_short_daily:
    'Company short — daily via enabled content plans (company:short:plans -> discover -> classify -> sample -> analyze per podcast scope)',
  email_weekly_ig_posts:
    'Email — weekly Instagram cards; Friday 08:00 review of last week, Monday 08:00 the week from two Mondays ago',
  email_daily_digest:
    'Email — daily pipeline status digest; 08:00 Chile (after morning batch)',
};

const ALL_JOB_KINDS = Object.keys(JOB_LABELS) as PipelineJobKind[];
/** Kinds that can be created as new schedules (retired / ad-hoc-only kinds excluded). */
const CREATABLE_JOB_KINDS = ALL_JOB_KINDS.filter(
  (k) => k !== 'explorer_long_pipeline' && k !== 'explorer_long_complete_pipeline',
);

function jobKindLabel(kind: string): string {
  if (kind === 'explorer_long_sample_transcribe') {
    return JOB_LABELS.explorer_long_complete_pipeline;
  }
  return JOB_LABELS[kind as PipelineJobKind] ?? kind;
}

const STEP_LABELS: Record<string, string> = {
  discover: 'Discover',
  classify_programs: 'Classify programs',
  sample: 'Sample',
  transcribe: 'Transcribe',
  visual_analyze: 'Visual analyze',
  analyze_brands: 'Analyze brands',
  merge: 'Merge mentions',
  analyze_placement: 'Analyze placement',
  lock_contention: 'Lock contention (skipped)',
  orchestrator_lock: 'Lock contention (skipped)',
};


const STATUS_CHIP = {
  success: { label: 'Success', color: 'success' as const, icon: <SuccessIcon sx={{ fontSize: 14 }} /> },
  failed: { label: 'Failed', color: 'error' as const, icon: <FailedIcon sx={{ fontSize: 14 }} /> },
  pending: { label: 'Pending', color: 'default' as const, icon: <PendingIcon sx={{ fontSize: 14 }} /> },
  running: { label: 'Running', color: 'info' as const, icon: <RunningIcon sx={{ fontSize: 14 }} /> },
  skipped: { label: 'Skipped', color: 'warning' as const, icon: <ScheduleIcon sx={{ fontSize: 14 }} /> },
};

function fmtDate(iso?: string | null) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('es-CL', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Minutes from midnight (0–1439) for sorting cron schedules by time of day. */
function cronSortKey(cronExpr: string): number {
  const parts = cronExpr.trim().split(/\s+/);
  if (parts.length < 2) return 24 * 60;
  const parseField = (field: string, max: number): number => {
    if (field === '*') return 0;
    const m = field.match(/^(\d+)/);
    if (!m) return 0;
    return Math.min(parseInt(m[1], 10), max);
  };
  const minute = parseField(parts[0], 59);
  const hour = parseField(parts[1], 23);
  return hour * 60 + minute;
}

function scheduleTimeSortKey(schedule: PipelineSchedule): number {
  if (schedule.nextRunAt) {
    return new Date(schedule.nextRunAt).getTime();
  }
  // Fall back to cron time-of-day when next run is unavailable.
  return cronSortKey(schedule.cronExpr);
}

function durationStr(startedAt?: string, finishedAt?: string) {
  if (!startedAt || !finishedAt) return '—';
  const ms = new Date(finishedAt).getTime() - new Date(startedAt).getTime();
  const sec = Math.round(ms / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  const rem = sec % 60;
  return `${min}m ${rem}s`;
}

// ─── Create Schedule dialog ───────────────────────────────────────────────────

interface CreateScheduleDialogProps {
  open: boolean;
  existingKinds: PipelineJobKind[];
  onClose: () => void;
}

function CreateScheduleDialog({ open, existingKinds, onClose }: CreateScheduleDialogProps) {
  const queryClient = useQueryClient();
  const availableKinds = CREATABLE_JOB_KINDS.filter((k) => !existingKinds.includes(k));

  const [kind, setKind] = useState<PipelineJobKind>(availableKinds[0] ?? 'explorer_long_discover_classify');
  const [time, setTime] = useState('');
  const [enabled, setEnabled] = useState(false);

  // Convert HH:MM to a standard 5-field daily cron expression: "MM HH * * *"
  const toCron = (hhmm: string) => {
    const [hh, mm] = hhmm.split(':');
    return `${parseInt(mm, 10)} ${parseInt(hh, 10)} * * *`;
  };

  const cronExpr = time ? toCron(time) : '';

  const { mutate: create, isPending, isError, error, reset } = useMutation({
    mutationFn: () =>
      apiService.createPipelineSchedule({ kind, cronExpr, enabled }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pipelineSchedules'] });
      onClose();
      reset();
    },
  });

  if (availableKinds.length === 0) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle>Create schedule</DialogTitle>
        <DialogContent>
          <Alert severity="info">All job kinds already have a schedule.</Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create schedule</DialogTitle>
      <DialogContent>
        <Stack spacing={3} mt={1}>
          <TextField
            select
            label="Job"
            value={kind}
            onChange={(e) => setKind(e.target.value as PipelineJobKind)}
            fullWidth
          >
            {availableKinds.map((k) => (
              <MenuItem key={k} value={k}>
                <Box>
                  <Typography variant="body2" fontWeight={600}>{JOB_LABELS[k]}</Typography>
                  <Typography variant="caption" color="text.secondary">{k}</Typography>
                </Box>
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Run at (America/Santiago)"
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            fullWidth
            inputProps={{ step: 1 }}
            helperText={
              cronExpr
                ? <span>Stored as cron: <code>{cronExpr}</code></span>
                : 'Pick the daily time this job should run'
            }
          />

          <FormControlLabel
            control={
              <Switch
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
              />
            }
            label="Enable immediately"
          />
        </Stack>

        {isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {(error as any)?.response?.data?.error ?? (error as Error)?.message ?? 'Failed to create schedule'}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isPending}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => create()}
          disabled={isPending || !time}
          startIcon={isPending ? <CircularProgress size={16} /> : <AddIcon />}
        >
          {isPending ? 'Creating…' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Run Now dialog ───────────────────────────────────────────────────────────

interface RunNowDialogProps {
  open: boolean;
  schedule: PipelineSchedule | null;
  onClose: () => void;
}

function RunNowDialog({ open, schedule, onClose }: RunNowDialogProps) {
  const queryClient = useQueryClient();

  const { mutate: trigger, isPending, isError, error } = useMutation({
    mutationFn: () =>
      apiService.triggerPipelineRun({
        kind: schedule!.kind,
        tenantType: 'global',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pipelineRuns'] });
      onClose();
    },
  });

  if (!schedule) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Run now: {JOB_LABELS[schedule.kind] ?? schedule.kind}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary">
          This will insert a <strong>pending</strong> run that the Mac Mini worker will pick up on its
          next poll cycle. You can monitor the result in the Runs tab.
        </Typography>
        {isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {(error as Error)?.message ?? 'Failed to trigger run'}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={isPending ? <CircularProgress size={16} /> : <RunIcon />}
          onClick={() => trigger()}
          disabled={isPending}
        >
          {isPending ? 'Triggering…' : 'Run now'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Edit Schedule dialog ─────────────────────────────────────────────────────

const DOW_LABELS: Record<string, string> = {
  '*': 'Every day',
  '0': 'Sunday',
  '1': 'Monday',
  '2': 'Tuesday',
  '3': 'Wednesday',
  '4': 'Thursday',
  '5': 'Friday',
  '6': 'Saturday',
};

function cronToTime(cronExpr: string): string {
  const parts = cronExpr.trim().split(/\s+/);
  if (parts.length !== 5) return '';
  const [mm, hh] = parts;
  return `${hh.padStart(2, '0')}:${mm.padStart(2, '0')}`;
}

function cronToDow(cronExpr: string): string {
  const parts = cronExpr.trim().split(/\s+/);
  return parts[4] ?? '*';
}

interface EditScheduleDialogProps {
  open: boolean;
  schedule: PipelineSchedule | null;
  onClose: () => void;
}

function EditScheduleDialog({ open, schedule, onClose }: EditScheduleDialogProps) {
  const queryClient = useQueryClient();
  const [time, setTime] = useState('');
  const [dow, setDow] = useState('*');
  const [enabled, setEnabled] = useState(false);

  React.useEffect(() => {
    if (schedule) {
      setTime(cronToTime(schedule.cronExpr));
      setDow(cronToDow(schedule.cronExpr));
      setEnabled(schedule.enabled);
    }
  }, [schedule]);

  const buildCron = () => {
    const [hh, mm] = time.split(':');
    return `${parseInt(mm, 10)} ${parseInt(hh, 10)} * * ${dow}`;
  };

  const { mutate: save, isPending, isError, error, reset } = useMutation({
    mutationFn: () =>
      apiService.updatePipelineSchedule(schedule!._id, {
        cronExpr: time ? buildCron() : schedule!.cronExpr,
        enabled,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pipelineSchedules'] });
      onClose();
      reset();
    },
  });

  if (!schedule) return null;

  const cronPreview = time ? buildCron() : schedule.cronExpr;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit schedule: {JOB_LABELS[schedule.kind] ?? schedule.kind}</DialogTitle>
      <DialogContent>
        <Stack spacing={3} mt={1}>
          <TextField
            label="Run at (America/Santiago)"
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            fullWidth
            inputProps={{ step: 60 }}
            helperText={<span>Stored as cron: <code>{cronPreview}</code></span>}
          />
          <TextField
            select
            label="Day of week"
            value={dow}
            onChange={(e) => setDow(e.target.value)}
            fullWidth
          >
            {Object.entries(DOW_LABELS).map(([val, label]) => (
              <MenuItem key={val} value={val}>{label}</MenuItem>
            ))}
          </TextField>
          <FormControlLabel
            control={<Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />}
            label="Enabled"
          />
        </Stack>
        {isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {(error as any)?.response?.data?.error ?? (error as Error)?.message ?? 'Failed to save'}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isPending}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => save()}
          disabled={isPending}
          startIcon={isPending ? <CircularProgress size={16} /> : <EditIcon />}
        >
          {isPending ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Delete Schedule dialog ───────────────────────────────────────────────────

interface DeleteScheduleDialogProps {
  open: boolean;
  schedule: PipelineSchedule | null;
  onClose: () => void;
}

function DeleteScheduleDialog({ open, schedule, onClose }: DeleteScheduleDialogProps) {
  const queryClient = useQueryClient();

  const { mutate: remove, isPending, isError, error } = useMutation({
    mutationFn: () => apiService.deletePipelineSchedule(schedule!._id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pipelineSchedules'] });
      onClose();
    },
  });

  if (!schedule) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Delete schedule</DialogTitle>
      <DialogContent>
        <Typography variant="body2">
          Delete the schedule for <strong>{JOB_LABELS[schedule.kind] ?? schedule.kind}</strong>?
          Run history will be kept. You can recreate it anytime.
        </Typography>
        {isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {(error as any)?.response?.data?.error ?? 'Failed to delete'}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isPending}>Cancel</Button>
        <Button
          variant="contained"
          color="error"
          onClick={() => remove()}
          disabled={isPending}
          startIcon={isPending ? <CircularProgress size={16} /> : <DeleteIcon />}
        >
          {isPending ? 'Deleting…' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Schedules tab ────────────────────────────────────────────────────────────

function SchedulesTab() {
  const queryClient = useQueryClient();
  const [runTarget, setRunTarget] = useState<PipelineSchedule | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<PipelineSchedule | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PipelineSchedule | null>(null);
  const [cronSort, setCronSort] = useState<'asc' | 'desc'>('asc');

  const { data: schedules, isLoading, error, refetch } = useQuery({
    queryKey: ['pipelineSchedules'],
    queryFn: apiService.getPipelineSchedules,
    refetchInterval: 30_000,
  });

  const { mutate: toggle } = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      apiService.togglePipelineSchedule(id, enabled),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['pipelineSchedules'] }),
  });

  const sortedSchedules = useMemo(() => {
    const list = [...(schedules ?? [])];
    list.sort((a, b) => {
      const diff = scheduleTimeSortKey(a) - scheduleTimeSortKey(b);
      if (diff !== 0) return cronSort === 'asc' ? diff : -diff;
      return a.kind.localeCompare(b.kind);
    });
    return list;
  }, [schedules, cronSort]);

  if (isLoading) {
    return (
      <Stack spacing={2} mt={2}>
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} variant="rounded" height={60} />
        ))}
      </Stack>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Failed to load schedules. Make sure you are logged in as admin.
      </Alert>
    );
  }

  const existingKinds = (schedules ?? []).map((s) => s.kind);
  const allScheduled = CREATABLE_JOB_KINDS.every((k) => existingKinds.includes(k));

  return (
    <>
      <Box display="flex" justifyContent="flex-end" gap={1} mb={2}>
        <Button startIcon={<RefreshIcon />} onClick={() => refetch()} size="small">
          Refresh
        </Button>
        {!allScheduled && (
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
          >
            Create schedule
          </Button>
        )}
      </Box>

      {(schedules ?? []).length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No schedules yet. Create one to get started.
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Job</TableCell>
              <TableCell
                sortDirection={cronSort}
                onClick={() => setCronSort((d) => (d === 'asc' ? 'desc' : 'asc'))}
                sx={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
              >
                <TableSortLabel active direction={cronSort}>
                  Cron
                </TableSortLabel>
              </TableCell>
              <TableCell>Next run</TableCell>
              <TableCell>Last run</TableCell>
              <TableCell>Last status</TableCell>
              <TableCell align="center">Enabled</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedSchedules.map((s) => {
              const st = s.lastRunStatus;
              const chip = st ? STATUS_CHIP[st] : null;
              const staleTooltip = s.isStale
                ? `No successful run recorded in the expected window (last run: ${fmtDate(s.lastRunAt)})`
                : '';
              const statusTooltip = [s.lastErrorMessage, s.lastFailedStep ? `Failed at: ${STEP_LABELS[s.lastFailedStep] ?? s.lastFailedStep}` : '']
                .filter(Boolean)
                .join('\n') || '';
              return (
                <TableRow key={s._id} hover>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      {s.isStale && (
                        <Tooltip title={staleTooltip}>
                          <StaleIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                        </Tooltip>
                      )}
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {jobKindLabel(s.kind)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {s.kind}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <code style={{ fontSize: 12 }}>{s.cronExpr}</code>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color={s.isStale && s.enabled ? 'warning.main' : 'text.secondary'}>
                      {fmtDate(s.nextRunAt)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">{fmtDate(s.lastRunAt)}</Typography>
                  </TableCell>
                  <TableCell>
                    {chip ? (
                      <Tooltip title={statusTooltip}>
                        <Box display="inline-flex" flexDirection="column" gap={0.25} alignItems="flex-start">
                          <Chip
                            size="small"
                            icon={chip.icon}
                            label={chip.label}
                            color={chip.color}
                          />
                          {s.lastFailedStep && (
                            <Typography variant="caption" color="text.secondary" sx={{ pl: 0.25 }}>
                              at: {STEP_LABELS[s.lastFailedStep] ?? s.lastFailedStep}
                            </Typography>
                          )}
                        </Box>
                      </Tooltip>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        No runs yet
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      size="small"
                      checked={s.enabled}
                      onChange={(e) => toggle({ id: s._id, enabled: e.target.checked })}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Run now">
                      <IconButton size="small" color="primary" onClick={() => setRunTarget(s)}>
                        <RunIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => setEditTarget(s)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => setDeleteTarget(s)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <CreateScheduleDialog
        open={createOpen}
        existingKinds={existingKinds}
        onClose={() => setCreateOpen(false)}
      />

      <EditScheduleDialog
        open={!!editTarget}
        schedule={editTarget}
        onClose={() => setEditTarget(null)}
      />

      <DeleteScheduleDialog
        open={!!deleteTarget}
        schedule={deleteTarget}
        onClose={() => setDeleteTarget(null)}
      />

      <RunNowDialog
        open={!!runTarget}
        schedule={runTarget}
        onClose={() => setRunTarget(null)}
      />
    </>
  );
}

// ─── Runs tab ─────────────────────────────────────────────────────────────────

const STATUS_FILTER_OPTIONS = ['', 'pending', 'running', 'success', 'failed'] as const;

function RunsTab() {
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const limit = 25;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['pipelineRuns', page, statusFilter],
    queryFn: () =>
      apiService.getPipelineRuns({
        page: page + 1,
        limit,
        status: statusFilter || undefined,
      }),
    refetchInterval: 10_000,
  });

  return (
    <>
      <Box display="flex" alignItems="center" gap={2} mb={2} flexWrap="wrap">
        <Typography variant="body2" color="text.secondary">
          Filter:
        </Typography>
        {STATUS_FILTER_OPTIONS.map((s) => (
          <Chip
            key={s || 'all'}
            label={s || 'All'}
            size="small"
            variant={statusFilter === s ? 'filled' : 'outlined'}
            color={s === 'failed' ? 'error' : s === 'success' ? 'success' : s === 'running' ? 'info' : 'default'}
            onClick={() => {
              setStatusFilter(s);
              setPage(0);
            }}
            sx={{ cursor: 'pointer' }}
          />
        ))}
        <Box flex={1} />
        <Button startIcon={<RefreshIcon />} onClick={() => refetch()} size="small">
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load runs.
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Job</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Step</TableCell>
              <TableCell>Trigger</TableCell>
              <TableCell>Requested</TableCell>
              <TableCell>Duration</TableCell>
              <TableCell>Error</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading
              ? [...Array(10)].map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={7}>
                      <Skeleton variant="text" />
                    </TableCell>
                  </TableRow>
                ))
              : (data?.runs ?? []).map((run) => {
                  const chip = STATUS_CHIP[run.status] ?? STATUS_CHIP.pending;
                  return (
                    <TableRow key={run._id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {jobKindLabel(run.kind)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          icon={chip.icon}
                          label={chip.label}
                          color={chip.color}
                        />
                      </TableCell>
                      <TableCell>
                        {run.failedStep ? (
                          <Typography variant="caption" color="error.main">
                            {STEP_LABELS[run.failedStep] ?? run.failedStep}
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="text.disabled">—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={run.trigger}
                          variant="outlined"
                          color={run.trigger === 'manual' ? 'warning' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">{fmtDate(run.requestedAt)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {durationStr(run.startedAt, run.finishedAt)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 300 }}>
                        {run.errorMessage ? (
                          <Tooltip title={run.errorMessage}>
                            <Typography
                              variant="caption"
                              color="error"
                              sx={{
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                cursor: 'help',
                              }}
                            >
                              {run.errorMessage}
                            </Typography>
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
          </TableBody>
        </Table>
      </TableContainer>

      {data && (
        <Box display="flex" justifyContent="flex-end" mt={1} alignItems="center" gap={2}>
          <Typography variant="caption" color="text.secondary">
            {data.pagination.total} total runs
          </Typography>
          <Button
            size="small"
            disabled={page === 0}
            onClick={() => setPage((p) => p - 1)}
          >
            Prev
          </Button>
          <Typography variant="caption">
            {page + 1} / {data.pagination.pages || 1}
          </Typography>
          <Button
            size="small"
            disabled={page + 1 >= data.pagination.pages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </Box>
      )}
    </>
  );
}

// ─── Stats strip ──────────────────────────────────────────────────────────────

function StatsStrip() {
  const { data } = useQuery({
    queryKey: ['pipelineRuns', 0, ''],
    queryFn: () => apiService.getPipelineRuns({ page: 1, limit: 1 }),
  });

  const { data: failed } = useQuery({
    queryKey: ['pipelineRuns', 0, 'failed'],
    queryFn: () => apiService.getPipelineRuns({ page: 1, limit: 1, status: 'failed' }),
  });

  const { data: running } = useQuery({
    queryKey: ['pipelineRuns', 0, 'running'],
    queryFn: () => apiService.getPipelineRuns({ page: 1, limit: 1, status: 'running' }),
  });

  const stats = [
    { label: 'Total runs', value: data?.pagination.total ?? '—', color: 'text.primary' },
    { label: 'Failed', value: failed?.pagination.total ?? '—', color: 'error.main' },
    { label: 'Currently running', value: running?.pagination.total ?? '—', color: 'info.main' },
  ];

  return (
    <Box display="flex" gap={2} mb={3} flexWrap="wrap">
      {stats.map((s) => (
        <Card key={s.label} variant="outlined" sx={{ minWidth: 150, flex: 1 }}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="h5" color={s.color} fontWeight={700}>
              {s.value}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {s.label}
            </Typography>
          </CardContent>
        </Card>
      ))}
    </Box>
  );
}

// ─── Company content plans ────────────────────────────────────────────────────

interface EditPlanDialogProps {
  open: boolean;
  plan: CompanyContentPlan | null;
  onClose: () => void;
}

function blankScope(): ContentPlanScope {
  return blankContentPlanScope();
}

function EditPlanDialog({ open, plan, onClose }: EditPlanDialogProps) {
  const queryClient = useQueryClient();
  const isEdit = !!plan;

  const [companyId, setCompanyId] = useState('');
  const [label, setLabel] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [scopes, setScopes] = useState<ContentPlanScope[]>([blankScope()]);

  useEffect(() => {
    if (!open) return;
    if (plan) {
      setCompanyId(plan.companyId);
      setLabel(plan.label ?? '');
      setEnabled(plan.enabled);
      setScopes(
        plan.scopes.length > 0
          ? plan.scopes.map((s) => ({
              podcastId: s.podcastId,
              programIds: s.programIds ?? [],
              brandIds: s.brandIds ?? [],
            }))
          : [blankScope()],
      );
    } else {
      setCompanyId('');
      setLabel('');
      setEnabled(false);
      setScopes([blankScope()]);
    }
  }, [open, plan]);

  const { data: companies } = useQuery({
    queryKey: ['companiesLite'],
    queryFn: apiService.getCompaniesLite,
    enabled: open,
  });

  const { mutate: save, isPending, isError, error, reset } = useMutation({
    mutationFn: () => {
      const payload: CompanyContentPlanInput = {
        companyId,
        label: label.trim() || undefined,
        enabled,
        scopes: scopes
          .filter((s) => !!s.podcastId)
          .map((s) => ({
            podcastId: s.podcastId,
            programIds: s.programIds && s.programIds.length > 0 ? s.programIds : undefined,
            brandIds: s.brandIds && s.brandIds.length > 0 ? s.brandIds : undefined,
          })),
      };
      return isEdit
        ? apiService.updateCompanyContentPlan(plan!._id, payload)
        : apiService.createCompanyContentPlan(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companyContentPlans'] });
      reset();
      onClose();
    },
  });

  const validScopes = scopes.filter((s) => !!s.podcastId);
  const canSave = !!companyId && validScopes.length > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEdit ? 'Edit content plan' : 'New content plan'}</DialogTitle>
      <DialogContent>
        <Stack spacing={3} mt={1}>
          <Autocomplete
            options={companies ?? []}
            getOptionLabel={(o) => o.name}
            value={(companies ?? []).find((c) => c._id === companyId) ?? null}
            onChange={(_, v) => {
              setCompanyId(v?._id ?? '');
              setScopes([blankScope()]);
            }}
            renderInput={(params) => (
              <TextField {...params} label="Company" required helperText="Used by company:mentions:plans (programs respected) and company:short:plans (programs ignored)" />
            )}
            isOptionEqualToValue={(o, v) => o._id === v._id}
          />

          <TextField
            label="Label (optional)"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            fullWidth
            placeholder="e.g. Acme — main shows"
          />

          <FormControlLabel
            control={<Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />}
            label="Enabled (included by default in CLI runs)"
          />

          <Divider />

          <ContentPlanScopesEditor
            companyId={companyId}
            scopes={scopes}
            onChange={setScopes}
            showBrands
            disabled={!companyId}
          />

          {isError && (
            <Alert severity="error">
              {(error as any)?.response?.data?.error ?? (error as Error)?.message ?? 'Failed to save plan'}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isPending}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => save()}
          disabled={isPending || !canSave}
          startIcon={isPending ? <CircularProgress size={16} /> : isEdit ? <EditIcon /> : <AddIcon />}
        >
          {isPending ? 'Saving…' : isEdit ? 'Save' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

interface DeletePlanDialogProps {
  open: boolean;
  plan: CompanyContentPlan | null;
  onClose: () => void;
}

function DeletePlanDialog({ open, plan, onClose }: DeletePlanDialogProps) {
  const queryClient = useQueryClient();
  const { mutate: remove, isPending, isError, error } = useMutation({
    mutationFn: () => apiService.deleteCompanyContentPlan(plan!._id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companyContentPlans'] });
      onClose();
    },
  });

  if (!plan) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Delete content plan</DialogTitle>
      <DialogContent>
        <Typography variant="body2">
          Delete the plan for <strong>{plan.companyName ?? plan.companyId}</strong>
          {plan.label ? ` ("${plan.label}")` : ''}?
        </Typography>
        {isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {(error as any)?.response?.data?.error ?? 'Failed to delete'}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isPending}>Cancel</Button>
        <Button
          variant="contained"
          color="error"
          onClick={() => remove()}
          disabled={isPending}
          startIcon={isPending ? <CircularProgress size={16} /> : <DeleteIcon />}
        >
          {isPending ? 'Deleting…' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ContentPlansTab() {
  const queryClient = useQueryClient();
  const [editTarget, setEditTarget] = useState<CompanyContentPlan | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<CompanyContentPlan | null>(null);

  const { data: plans, isLoading, error, refetch } = useQuery({
    queryKey: ['companyContentPlans'],
    queryFn: apiService.getCompanyContentPlans,
    refetchInterval: 30_000,
  });

  const { mutate: toggle } = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      apiService.updateCompanyContentPlan(id, { enabled }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['companyContentPlans'] }),
  });

  if (isLoading) {
    return (
      <Stack spacing={2} mt={2}>
        {[...Array(4)].map((_, i) => (
          <Skeleton key={i} variant="rounded" height={70} />
        ))}
      </Stack>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Failed to load content plans. Make sure you are logged in as admin.
      </Alert>
    );
  }

  return (
    <>
      <Alert severity="info" sx={{ mb: 2 }}>
        These plans store the <strong>company → podcasts → programs</strong> mapping shared by both
        company-scoped pipeline runners. Execute on the worker host:
        <Box component="pre" sx={{ mt: 1, mb: 0, p: 1, bgcolor: 'action.hover', borderRadius: 1, fontSize: 12, overflow: 'auto' }}>
{`# Mentions chain (uses programIds when set on each scope)
yarn workspace @podcastradar/pipelines company:mentions:plans \\
  --date-from YYYY-MM-DD --date-to YYYY-MM-DD [--force] [--company-id <id>] [--podcast-id <id>] [--include-disabled] [--dry-run]

# Short-content chain (podcast-level only — programIds are ignored, since
# short content has no program classification yet at this stage)
yarn workspace @podcastradar/pipelines company:short:plans \\
  --date-from YYYY-MM-DD --date-to YYYY-MM-DD [--force] [--company-id <id>] [--podcast-id <id>] [--include-disabled] [--dry-run]`}
        </Box>
      </Alert>

      <Box display="flex" justifyContent="flex-end" gap={1} mb={2}>
        <Button startIcon={<RefreshIcon />} onClick={() => refetch()} size="small">
          Refresh
        </Button>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
        >
          New plan
        </Button>
      </Box>

      {(plans ?? []).length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No content plans yet. Create one to map a company's podcasts and programs.
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Company</TableCell>
              <TableCell>Label</TableCell>
              <TableCell>Podcasts</TableCell>
              <TableCell>Programs</TableCell>
              <TableCell align="center">Enabled</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(plans ?? []).map((p) => {
              const programCount = p.scopes.reduce((acc, s) => acc + (s.programIds?.length ?? 0), 0);
              return (
                <TableRow key={p._id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {p.companyName ?? '—'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {p.companyId}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">{p.label ?? '—'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={`${p.scopes.length} podcast${p.scopes.length === 1 ? '' : 's'}`} />
                  </TableCell>
                  <TableCell>
                    {programCount > 0 ? (
                      <Chip size="small" label={`${programCount} program${programCount === 1 ? '' : 's'}`} />
                    ) : (
                      <Typography variant="caption" color="text.secondary">all</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      size="small"
                      checked={p.enabled}
                      onChange={(e) => toggle({ id: p._id, enabled: e.target.checked })}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => setEditTarget(p)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => setDeleteTarget(p)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <EditPlanDialog
        open={createOpen}
        plan={null}
        onClose={() => setCreateOpen(false)}
      />

      <EditPlanDialog
        open={!!editTarget}
        plan={editTarget}
        onClose={() => setEditTarget(null)}
      />

      <DeletePlanDialog
        open={!!deleteTarget}
        plan={deleteTarget}
        onClose={() => setDeleteTarget(null)}
      />
    </>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function PipelinesAdmin() {
  const [tab, setTab] = useState(0);

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box mb={3} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            Pipeline Orchestrator
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Schedule management and run history for all data pipelines. Jobs are executed on the Mac
            Mini worker — use "Run now" to queue a manual run.
          </Typography>
        </Box>
        <Button
          component={RouterLink}
          to={ROUTES_NAMES.ADMIN_PIPELINE_DIGEST_PREVIEW}
          variant="outlined"
          size="small"
        >
          Email preview
        </Button>
      </Box>

      <StatsStrip />

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Schedules" />
        <Tab label="Run history" />
        <Tab label="Content plans" />
      </Tabs>

      {tab === 0 && <SchedulesTab />}
      {tab === 1 && <RunsTab />}
      {tab === 2 && <ContentPlansTab />}
    </Container>
  );
}
